import area from "@turf/area";
import { useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { MapLayerMouseEvent, MapSourceDataEvent, useMap } from "react-map-gl/maplibre";

import { makeUnique } from "@nens/shared-utils";

import { VECTOR_LAYER_ID } from "@/config/map-layer-constants";
import { getRanaWidgetFromProperty } from "@/features/project-process-run-form";

import { FeatureSelectorFieldProps } from "../types/feature-selector-field-props-type";
import { featureSelectorGetMapId } from "../utils/feature-selector-get-map-id";
import { getFeaturePlaceName } from "../utils/get-feature-place-name";

export function useSelectFeature(
  props: FeatureSelectorFieldProps,
  layerNames: string[],
  mapSuffix: string
) {
  const intl = useIntl();
  const { onChange, readonly, formData, schema } = props;
  const { [featureSelectorGetMapId(props, mapSuffix)]: map } = useMap();

  const widget = getRanaWidgetFromProperty(schema, "file_feature_selector");
  if (!widget) throw new Error("File Feature Selector widget configuration not found in schema");
  const maxAreaSquareKm = widget.max_area_square_km;

  // FUTURE: this doesnt work correctly because it only queries the rendered map. Not the complete feature.
  const areaError = useMemo(() => {
    const layerName = formData?.layer_name;
    const ids = formData?.feature_ids ?? [];
    if (!maxAreaSquareKm || !ids.length || !map || !layerName) return undefined;
    const sourceFeatures = makeUnique(
      map.querySourceFeatures(layerName, { sourceLayer: VECTOR_LAYER_ID }),
      ({ id }) => id + ""
    ).filter((f) => ids.includes(Number(f.id)));
    const totalKm2 = area({ type: "FeatureCollection", features: sourceFeatures }) / 1e6;
    if (totalKm2 <= maxAreaSquareKm) return undefined;
    return intl.formatMessage(
      {
        id: "process.selected_area_exceeds_maximum",
        defaultMessage:
          "Selected area ({totalKm2} km²) exceeds the maximum of {maxAreaSquareKm} km².",
      },
      { totalKm2: totalKm2.toFixed(1), maxAreaSquareKm }
    );
  }, [formData, maxAreaSquareKm, map, intl]);

  const lastLayerNames = useRef<typeof layerNames | null>(
    formData?.layer_name ? [formData.layer_name] : null
  );
  useEffect(() => {
    if (!map) return;

    // Remove selected geojsons if new data is loaded and has more then 1 feature
    const resetSelectedFeatures = () => onChange(undefined);

    // Empty data if all layers are removed
    if (!layerNames.length && lastLayerNames.current?.length) {
      lastLayerNames.current = null;
      resetSelectedFeatures();
    }

    const getMapFeatures = (sourceId: string) => {
      // Features can appear in multiple tiles when they span tile boundaries,
      // We deduplicate the array to ensure each feature is processed only once.
      return makeUnique(
        map.querySourceFeatures(sourceId, {
          sourceLayer: VECTOR_LAYER_ID,
        }),
        ({ id }) => id + ""
      );
    };

    const onSourceDataChange = (e: MapSourceDataEvent) => {
      // Only call function if a new layer data is loaded and it has features
      if (
        readonly ||
        !e.isSourceLoaded ||
        !layerNames.includes(e.sourceId) ||
        !getMapFeatures(e.sourceId).length ||
        lastLayerNames.current?.toString() === layerNames.toString()
      )
        return;
      lastLayerNames.current = layerNames;

      // If there are multiple layers there will be mutltiple features
      if (layerNames.length !== 1) return resetSelectedFeatures();

      const layerName = layerNames[0];
      const features = getMapFeatures(layerName);

      // If only 1 feature set that as active
      if (features.length === 1) {
        const onlyFeature = features[0];

        onChange({
          feature_ids: [Number(onlyFeature.id)],
          feature_names: [getFeaturePlaceName(onlyFeature) ?? ""],
          layer_name: layerName,
        });
      } else {
        resetSelectedFeatures();
      }
    };

    map.on("sourcedata", onSourceDataChange);
    return () => {
      map.off("sourcedata", onSourceDataChange);
    };
  }, [formData, layerNames, map, onChange, readonly]);

  const onClick = (event: MapLayerMouseEvent) => {
    if (!map) return;

    const features = map.queryRenderedFeatures(event.point, {
      layers: layerNames, // Only query features from these layers
    });

    if (!features.length) return;

    const feature = features[0];

    const featureId = Number(feature.id);
    const layerName = feature.layer.id;

    if (formData?.layer_name !== layerName) {
      onChange({
        layer_name: layerName,
        feature_ids: [featureId],
        feature_names: [getFeaturePlaceName(feature) ?? ""],
      });
      return;
    }

    const featureIds = [...(formData.feature_ids ?? [])];
    const featureNames = [...(formData.feature_names ?? [])];

    const selectedFeatureIndex = featureIds.findIndex((id) => id === featureId);

    if (selectedFeatureIndex === -1) {
      featureIds.push(featureId);
      featureNames.push(getFeaturePlaceName(feature) ?? "");
    } else {
      featureIds.splice(selectedFeatureIndex, 1);
      featureNames.splice(selectedFeatureIndex, 1);
    }

    onChange(
      featureIds.length
        ? {
            feature_ids: featureIds,
            feature_names: featureNames,
            layer_name: layerName,
          }
        : undefined
    );
  };

  return {
    onClick,
    areaError,
  } as const;
}
